import { useCallback, useState } from "react";
import {
  BaseResponse,
  FaqCreateRequest,
  FaqItem,
  FaqListResponse,
  FaqModifyRequest,
  FaqUpdateRequest,
} from "../../types/faq";
import { api } from "../../utils/api";

interface ServiceFaqState {
  faqList: FaqItem[];
  totalCount: number;
  currentPage: number;
}

export const useServiceFaq = () => {
  const [state, setState] = useState<ServiceFaqState>({
    faqList: [],
    totalCount: 0,
    currentPage: 1,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleError = (error: any) => {
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      "알 수 없는 오류가 발생했습니다.";
    setError(errorMessage);
  };

  const fetchFaqList = useCallback(async (page: number = 1) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.get<FaqListResponse>(
        `/admin/v1/faq/list?page=${page}`
      );

      if (response.data.status) {
        setState((prev) => ({
          ...prev,
          faqList: response.data.data,
          totalCount: response.data.data.length,
          currentPage: page,
        }));
      } else {
        throw new Error("API 응답 상태가 실패입니다.");
      }
    } catch (error) {
      handleError(error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createFaq = useCallback(
    async (faqData: FaqCreateRequest) => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await api.post<BaseResponse>(
          `/admin/v1/faq/gen`,
          faqData
        );

        if (response.data.status) {
          await fetchFaqList(state.currentPage);
          return true;
        } else {
          throw new Error("FAQ 생성에 실패했습니다.");
        }
      } catch (error) {
        handleError(error);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchFaqList, state.currentPage]
  );

  const updateFaq = useCallback(
    async (id: string, updateData: FaqUpdateRequest) => {
      setIsLoading(true);
      setError(null);

      try {
        const requestData: FaqModifyRequest = {
            id,
            title: updateData.title,
            description: updateData.description,
            reference_image: updateData.reference_image || "",
            delete: false,
        };

        const response = await api.post<BaseResponse>(
            `/admin/v1/faq/update`,
            requestData
        );

        if (response.data.status) {
            await fetchFaqList(state.currentPage);
            return true;
        } else {
            throw new Error("FAQ 수정에 실패했습니다.");
        }
      } catch (error) {
        handleError(error);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchFaqList, state.currentPage]
  );

  const resetState = useCallback(() => {
    setState({
      faqList: [],
      totalCount: 0,
      currentPage: 1,
    });
    setError(null);
  }, []);

  const setCurrentPage = useCallback((page: number) => {
    setState((prev) => ({
      ...prev,
      currentPage: page,
    }));
  }, []);

  return {
    faqList: state.faqList,
    totalCount: state.totalCount,
    currentPage: state.currentPage,

    isLoading,
    error,

    fetchFaqList,
    createFaq,
    updateFaq,

    resetState,
    setCurrentPage,

    totalPages: Math.ceil(state.totalCount / 10),
    hasFaqs: state.faqList.length > 0,
    isEmpty: state.faqList.length === 0 && !isLoading,
  };
};
